import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { SkillSummary } from '../../types/team.js';

@customElement('cp-team-card')
export class CpTeamCard extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Object }) skill!: SkillSummary;
  @property({ type: Array }) availableMcpTools: string[] = [];

  private _getCategoryBadgeClass(): string {
    const cat = this.skill.category?.toLowerCase() || '';
    switch (cat) {
      case 'frontend':
      case 'custom': return 'cp-team-card__badge--category-frontend';
      case 'backend': return 'cp-team-card__badge--category-backend';
      case 'domain': return 'cp-team-card__badge--category-domain';
      case 'architect': return 'cp-team-card__badge--category-architect';
      case 'quality': return 'cp-team-card__badge--category-quality';
      case 'po': return 'cp-team-card__badge--category-po';
      case 'review': return 'cp-team-card__badge--category-review';
      default: return 'cp-team-card__badge--category';
    }
  }

  private _getTeamTypeBadgeClass(): string {
    switch (this.skill.teamType) {
      case 'team': return 'cp-team-card__badge--team-type-team';
      case 'individual': return 'cp-team-card__badge--team-type-individual';
      default: return 'cp-team-card__badge--team-type';
    }
  }

  private _getTeamTypeLabel(): string {
    switch (this.skill.teamType) {
      case 'team': return 'Team';
      case 'individual': return 'Individual';
      default: return 'DevTeam';
    }
  }

  private _handleClick(): void {
    this.dispatchEvent(new CustomEvent('card-click', {
      detail: { skillId: this.skill.id },
      bubbles: true,
      composed: true,
    }));
  }

  private _handleEditClick(e: Event): void {
    e.stopPropagation();
    this.dispatchEvent(new CustomEvent('edit-click', {
      detail: { skillId: this.skill.id },
      bubbles: true,
      composed: true,
    }));
  }

  private _handleCallClick(e: Event): void {
    e.stopPropagation();
    this.dispatchEvent(new CustomEvent('call-click', {
      detail: { skillId: this.skill.id },
      bubbles: true,
      composed: true,
    }));
  }

  private _handleDeleteClick(e: Event): void {
    e.stopPropagation();
    this.dispatchEvent(new CustomEvent('delete-click', {
      detail: { skillId: this.skill.id, skillName: this.skill.name, teamType: this.skill.teamType },
      bubbles: true,
      composed: true,
    }));
  }

  render() {
    return html`
      <div class="cp-team-card" @click=${this._handleClick}>
        <div class="cp-team-card__header">
          <h3 class="cp-team-card__name">${this.skill.name}</h3>
          <div class="cp-team-card__badges">
            <span class="cp-team-card__badge ${this._getTeamTypeBadgeClass()}">${this._getTeamTypeLabel()}</span>
            <span class="cp-team-card__badge ${this._getCategoryBadgeClass()}">${this.skill.category}</span>
          </div>
        </div>
        <p class="cp-team-card__description">${this.skill.description || 'Keine Beschreibung vorhanden.'}</p>
        <div class="cp-team-card__footer">
          <span class="cp-team-card__learnings">
            ${this.skill.learningsCount} ${this.skill.learningsCount === 1 ? 'Learning' : 'Learnings'}
          </span>
          <div class="cp-team-card__footer-right">
            ${this.skill.mcpTools && this.skill.mcpTools.length > 0 ? html`
              <div class="cp-team-card__mcp-badges">
                ${this.skill.mcpTools.map(tool => {
                  const isOrphaned = this.availableMcpTools.length > 0 && !this.availableMcpTools.includes(tool);
                  return html`
                    <span
                      class="cp-team-card__mcp-badge ${isOrphaned ? 'cp-team-card__mcp-badge--orphaned' : ''}"
                      title=${isOrphaned ? 'MCP Tool nicht verfuegbar' : tool}
                    >${tool}</span>
                  `;
                })}
              </div>
            ` : nothing}
            ${this.skill.alwaysApply ? html`<span class="cp-team-card__always-active">Always Active</span>` : nothing}
          </div>
          <div class="cp-team-card__actions">
            <button class="cp-team-card__action-btn cp-team-card__action-btn--call" @click=${this._handleCallClick} title="Anrufen">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
            </button>
            <button class="cp-team-card__action-btn" @click=${this._handleEditClick} title="Bearbeiten">&#9998;</button>
            <button class="cp-team-card__action-btn cp-team-card__action-btn--delete" @click=${this._handleDeleteClick} title="Loeschen">&#10005;</button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-team-card': CpTeamCard;
  }
}
